import type { ReactNode } from 'react'
import { useCallback, useContext, useMemo } from 'react'
import AdditionalGuestsPopup from 'components/Common/AdditionalGuestsPopup/AdditionalGuestsPopup'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import type { OfferPagePriceMode, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { includedGuestsExceededItems } from 'lib/checkout/cartReservationUtils'
import { getEffectiveBundledWithFlightsOnly, getEffectiveCharterBundleRoute, getOfferDestinationAirport } from 'selectors/offerPage/offerPageSelectors'
import moment from 'moment'
import { FLIGHTS_MAX_LEAD_TIME_DAYS, isRewardFareClass, toBaseFareClass, type FlightsClassTypes, type FareClassWithReward } from 'constants/flight'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import GeoContext from 'contexts/geoContext'
import AtolProtectedTooltip from 'components/OfferPage/AtolProtectedTooltip'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import Pill from 'LuxKit/Pill'
import OfferBookButton from '../OfferBookButtons/OfferBookButton'
import SecureWithDepositTagAndModal from 'components/Common/SecureWithDeposit/SecureWithDepositTagAndModal'
import { isDepositAvailableOnOffer, getDepositAmountPercentage } from 'lib/payment/depositsUtils'
import { isFlexibleCancellationPolicy } from 'lib/offer/cancellationPolicyUtils'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import { isPackageEligibleForZeroReservation } from 'lib/offer/reserveForZeroUtils'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Clickable from 'components/Common/Clickable/Clickable'
import ReserveForZeroPolicyModal from 'components/Common/ReserveForZeroTagAndTooltip/ReserveForZeroPolicyModal'
import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import config from 'constants/config'
import Group from 'components/utils/Group'
import { max, sortBy, unique } from 'lib/array/arrayUtils'
import ModalContext from 'contexts/ModalContext'
import ReserveForZeroAndDepositPolicyModal from 'components/Common/ReserveForZeroTagAndTooltip/ReserveForZeroAndDepositPolicyModal'
import LineClockDollarIcon from 'LuxKit/Icons/line/LineClockDollarIcon'
import TextLink from 'LuxKit/TextLink'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import PriceRowValueDiscount from 'LuxKit/PricePoints/Value/PriceRowValueDiscount'
import useFlightDeals from 'hooks/Flights/useFlightDeals'
import FlightDealLabel from 'components/OfferPage/FlightDeal/FlightDealLabel'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import OfferPriceDetailsRow from 'components/Common/PriceDetails/OfferPriceDetailsRow'
import OfferPageBurnPriceDisplay from 'components/OfferPage/Loyalty/OfferPageBurnPriceDisplay'
import { isOfferPageFullyPaidWithPoints } from 'lib/offer/isOfferPageFullyPaidWithPoints'
import OfferPagePaidEntirelyWithPointsLabel from 'components/OfferPage/Loyalty/OfferPagePaidEntirelyWithPointsLabel'
import { useOfferPageBundledBurnPoints } from 'hooks/useOfferPageBurnPoints'
import getLoyaltyProductType from 'loyalty/lib/getLoyaltyProductType'
import getHotelEncryptedMargin from 'loyalty/lib/margin/getHotelEncryptedMargin'
import { addFlightPrice, feesToObject } from 'lib/offer/pricing'
import FlashHotelMarginText from './FlashHotelMarginText'
import FlashHotelInsufficientLoyaltyPointsCard from './FlashHotelInsufficientLoyaltyPointsCard'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import OfferAddToCartButton from '../OfferBookButtons/OfferAddToCartButton'
import OfferSelectPackageButton from '../OfferBookButtons/OfferSelectPackageButton'
import { offerPageCheckAvailabilityEvent } from 'analytics/snowplow/events'
import { generateLoyaltyPointsCalculatorBundledFlightOptions, generateLoyaltyPointsCalculatorHotelOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import { getPriceDisplayFunction } from 'lib/offer/offerPageUtil'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import OfferLabels from 'components/Common/Labels/OfferLabels'
import UrgencyLabel from 'LuxKit/Label/UrgencyLabel'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import { useOfferPageFlightPrice } from 'hooks/useFlightPrice'
import AgentHubAdditionalCommissionLabel from 'agentHub/components/AgentHubAdditionalCommissionLabel'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { formatGuestsDisplay } from 'lib/string/stringUtils'
import styled from 'styled-components'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import FormatCurrency from 'components/Common/FormatCurrency'
import FormatLoyaltyPoints from 'loyalty/components/Formatters/FormatLoyaltyPoints'
import { computeRewardBundlePrice, getRewardPriceDisplayState, REWARD_PREVIEW_RATE_BY_CABIN } from 'lib/maldivesCharter/classicRewardPricing'
import RewardBundlePriceDisplay from './RewardBundlePriceDisplay'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import { useCharterBundleFallbackPricing } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/useCharterBundleFallbackPricing'
import useCharterFlightDates from 'hooks/CharterFlights/useCharterFlightDates'
import { FormattedMessage, useIntl } from 'react-intl'
import { trackEvent } from 'analytics/analytics'
import AgentExclusivePackageLabel from 'components/Common/Labels/AgentExclusivePackageLabel'
import { canAddCharterBundleToUserCart, getFeederOriginAirportCode } from 'lib/charterFlights/charterBundleOfferPageUtils'
import { useAppSelector } from 'hooks/reduxHooks'
import BodyText from 'LuxKit/Typography/BodyText'
import getEffectiveHotelFlightPrice from 'lib/offer/getEffectiveHotelFlightPrice'

const FlexAutoGroup = styled(Group)`
  & > * {
    flex: auto;
  }
`

interface Props {
  offer: App.Offer
  roomsBeforeSurcharge?: number
  packageView: App.OfferPackageOptionView
  flightOrigin?: App.Airport
  flightDestination?: App.Airport
  rooms: Array<OfferPageRoom>
  checkIn?: string
  fetching?: boolean
  isSpoofed: boolean
  isStoreMode: boolean
  defaultDepositAmountPercentage: number
  isFlightsSelected: boolean
  charterBundleRoute?: App.CharterBundleRoute
  priceMode: OfferPagePriceMode
  fareClass: FlightsClassTypes
  bundledWithFlightsOnly: boolean
}

function FlashHotelOptionPriceDetails(props: Props) {
  const {
    offer,
    roomsBeforeSurcharge,
    flightOrigin,
    flightDestination,
    rooms,
    checkIn,
    fetching,
    isSpoofed,
    isStoreMode,
    defaultDepositAmountPercentage,
    isFlightsSelected,
    packageView,
    charterBundleRoute,
    priceMode,
    fareClass,
    bundledWithFlightsOnly,
  } = props

  const intl = useIntl()
  const {
    package: pkg,
    flightsAvailable,
    pricing,
    memberPricing,
  } = packageView
  const showAgentExclusivePackageLabel = !!pkg.isAgentHubExclusive && !offer.isAgentHubExclusive

  const checkInMoment = useMemo(() => checkIn ? moment(checkIn) : undefined, [checkIn])
  const {
    totalFlightPrice: flightPrice,
    loading: flightPriceLoading,
    loyaltyEarnKey,
    rewardAvailable,
    rewardSeatPointsTotal,
    rewardSeatCashCoPayTotal,
    hasFeederFlightPriced,
  } = useOfferPageFlightPrice()

  const effectiveFlightPrice = getEffectiveHotelFlightPrice({
    charterBundleRoute,
    checkIn,
    flightOriginCode: flightOrigin?.code,
    offer: offer as App.HotelOffer,
    totalFlightPrice: flightPrice,
  })
  const hotelLoyaltyProductType = getLoyaltyProductType(offer as App.HotelOffer) ?? 'hotel_flash'
  const hotelBurnMargin = getHotelEncryptedMargin(offer.type, pkg)
  const shouldUseSplitBurn = effectiveFlightPrice > 0 && (isFlightsSelected || bundledWithFlightsOnly)

  const burnResult = useOfferPageBundledBurnPoints({
    hotelPrice: pricing.price,
    hotelLoyaltyProductType,
    hotelBurnMargin,
    flightPrice: effectiveFlightPrice,
    shouldUseSplitBurn,
  })

  const { enabled: showPointsPrice, pointsToBurn: burnPointsToApply, priceInPoints: burnPriceInPoints, hideZeroCash: burnHideZeroCash } = burnResult
  const shouldShowPaidEntirelyWithPointsLabel = isOfferPageFullyPaidWithPoints(
    burnPointsToApply,
    burnPriceInPoints,
    burnHideZeroCash,
  )
  const renderBurnPriceDisplay = (totalPrice: number, memberPrice?: number) => {
    if (!showPointsPrice || burnPriceInPoints === undefined) {
      return null
    }

    return (
      <OfferPageBurnPriceDisplay
        totalPrice={totalPrice}
        pointsToApply={burnPointsToApply}
        priceInPoints={burnPriceInPoints}
        pointsHideZeroCash={burnHideZeroCash}
        memberPrice={memberPrice}
        size="L"
        saleUnit={priceMode === 'perPerson' ? 'person' : 'total'}
      />
    )
  }

  const pricingWithFlights = effectiveFlightPrice ? addFlightPrice(pricing, effectiveFlightPrice) : undefined
  const memberPricingWithFlights = (memberPricing && effectiveFlightPrice) ? addFlightPrice(memberPricing, effectiveFlightPrice) : undefined

  const fees = feesToObject(pricing.fees)

  const priceWithoutPropertyFees = pricing.price - (fees.property ?? 0)

  const [deals] = useFlightDeals('all', {
    originAirportCode: flightOrigin?.code,
    destinationAirportCode: flightDestination?.code,
  })
  const { trackEvent: trackOfferPageEvent } = useContext(OfferPageStateTrackingContext)
  const depositAmountPercentage = getDepositAmountPercentage(offer, defaultDepositAmountPercentage)
  const { currentRegionCode, currentCurrency } = useContext(GeoContext)

  const flightsEnabled = flightOrigin && offer.offerFlightsEnabled && (flightsAvailable || bundledWithFlightsOnly)
  const hasFlightPrice = !!pricingWithFlights
  const missingCharterFlightPrice = !!charterBundleRoute && !flightPrice
  const feederOriginAirportCode = getFeederOriginAirportCode(charterBundleRoute, flightOrigin)
  // Gate feeder-specific caption: only when at least one leg carries a positive
  // `feederEstimatedPrice` for the picked dates. Otherwise fall through to the
  // generic "& flights" caption — charter is still bundled, but the feeder leg
  // is not priced and shouldn't be advertised.
  const isFeederIncluded = !!feederOriginAirportCode && hasFeederFlightPriced

  // Get charter flight dates to check if user's checkIn matches available charter dates
  const { departDate: charterDepartDate } = useCharterFlightDates({
    checkInDate: checkIn,
    nights: pkg.duration,
    charterBundleRoute,
    rooms,
    fareClass,
  })

  // Check if user's checkIn matches a valid charter flight date
  const isCheckInMatchingCharterDate = !charterBundleRoute || (!!checkIn && !!charterDepartDate)

  // Only show error when checkIn is selected but flight price fetch failed
  const shouldShowCharterFlightErrorMessage = missingCharterFlightPrice && !flightPriceLoading && !!checkIn && isCheckInMatchingCharterDate
  const canBuyNow = !!checkIn && !!rooms && packageView.fromCalendarInfo && !shouldShowCharterFlightErrorMessage && isCheckInMatchingCharterDate

  // Determine if we should use fallback pricing for charter bundle
  // Use fallback when: no checkIn date selected, OR checkIn doesn't match any charter flight date
  const shouldUseFallbackPricing = useMemo(() => {
    if (!charterBundleRoute) return false
    // Use fallback when no checkIn date is selected, or checkIn doesn't match charter flight dates
    return !checkIn || !isCheckInMatchingCharterDate
  }, [charterBundleRoute, checkIn, isCheckInMatchingCharterDate])

  // Get fallback pricing for charter bundle when no dates are selected
  // Pass rooms from context - the hook has its own fallback for empty rooms
  const { fallbackPricing, fallbackMemberPricing } = useCharterBundleFallbackPricing({
    offer: offer as App.HotelOffer,
    rooms,
    charterBundleRoute,
    fareClass,
    nights: pkg.duration,
    disabled: !shouldUseFallbackPricing,
    packageUniqueKey: pkg.uniqueKey,
  })

  // Determine which pricing to display for charter bundle
  const charterPricingToDisplay = useMemo(() => {
    if (!charterBundleRoute) return { pricing, memberPricing, isUsingFallback: false }

    // If we have checkIn that matches charter date and have flight price, use the bundled pricing
    if (checkIn && isCheckInMatchingCharterDate && pricingWithFlights) {
      return {
        pricing: pricingWithFlights,
        memberPricing: memberPricingWithFlights,
        isUsingFallback: false,
      }
    }

    // If no checkIn or checkIn doesn't match charter date, use fallback pricing
    if ((!checkIn || !isCheckInMatchingCharterDate) && fallbackPricing) {
      return {
        pricing: fallbackPricing,
        memberPricing: fallbackMemberPricing,
        isUsingFallback: true,
      }
    }

    // Default to hotel pricing (loading state or error)
    return { pricing, memberPricing, isUsingFallback: false }
  }, [charterBundleRoute, checkIn, isCheckInMatchingCharterDate, pricingWithFlights, memberPricingWithFlights, fallbackPricing, fallbackMemberPricing, pricing, memberPricing])

  const showIncludedGuestsExceeded = useMemo(() => !pkg.roomRate?.extraGuestSurcharges.length && includedGuestsExceededItems(rooms || [], pkg, offer), [pkg, rooms, offer])
  const extraGuestSurchargesPayableAtProperty = !!pkg.roomRate?.extraGuestSurcharges.length && pkg.roomRate?.extraGuestSurcharges.some(surcharge => surcharge?.payableAtProperty)

  const shouldShowDiscountPercent = pricing.discountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const flightsBundledNoCache = !hasFlightPrice && bundledWithFlightsOnly
  const shouldShowBundledDiscountPercent = !!pricingWithFlights && pricingWithFlights.discountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const discountPercent = bundledWithFlightsOnly ? pricingWithFlights?.discountPercent : max([pricing.discountPercent, pricingWithFlights?.discountPercent ?? 0])
  const departureTooFarInFuture = useMemo(() => {
    return !!checkInMoment && checkInMoment.diff(moment.utc(), 'days') > FLIGHTS_MAX_LEAD_TIME_DAYS
  }, [checkInMoment])

  const showPriceDetails = !!fees.taxesAndFees || !!fees.property || config.agentHub.isEnabled
  const showMemberPrice = !!memberPricing && memberPricing.price > 0

  const canAddToUserCart = canBuyNow && canAddCharterBundleToUserCart({
    charterBundleRoute,
    flightOrigin,
  })
  const shouldShowDiscount = !offer.isDiscountPillHidden && (shouldShowDiscountPercent || shouldShowBundledDiscountPercent) && pkg.shouldDisplayValue && (bundledWithFlightsOnly ? hasFlightPrice : true)
  const shouldShowValue = pricing.value > pricing.price && pkg.shouldDisplayValue
  const isDepositEnabled = isDepositAvailableOnOffer(offer, currentCurrency, checkInMoment, pricing.price)
  const isIndiaRegion = currentRegionCode === 'IN'
  const isFlexibleCancellation = isFlexibleCancellationPolicy(packageView.package?.roomRate?.cancellationPolicy)

  const roomRateIsReservableForZero = isPackageEligibleForZeroReservation(offer, packageView.package.roomRate?.isReservableForZero, checkInMoment)
  const isReserveForZeroEnabled = config.RESERVE_FOR_ZERO_ENABLED
  const showReserveForZero = (isReserveForZeroEnabled || isSpoofed) && roomRateIsReservableForZero
  const showDepositTag = isDepositEnabled && !isSpoofed
  const showFlexiblePaymentTag = isDepositEnabled && isSpoofed

  const showFlightsDealTag = flightsEnabled && flightOrigin && flightDestination && deals.length > 0 && !charterBundleRoute

  const activeDeal = useMemo(
    () => (deals.length > 0 ? sortBy(deals, (d: App.FlightDeal) => d.perAdultPrice, 'asc')[0] : undefined),
    [deals],
  )

  // Hide the regular flights block when charter bundle is using fallback pricing
  const hideHotelAndFlightsPriceBlock = !isFlightsSelected || missingCharterFlightPrice || (!!charterBundleRoute && shouldUseFallbackPricing)
  const flightsBookable = flightOrigin && flightOrigin.code !== flightDestination?.code && !departureTooFarInFuture
  const showModal = useContext(ModalContext)
  const onSubmit = useCallback(() => {
    if (!canBuyNow) {
      const currentPrice = isFlightsSelected ? pricingWithFlights?.price : pricing.price

      if (currentPrice) {
        trackEvent(offerPageCheckAvailabilityEvent({ offer, roomRateId: pkg.roomRate?.id, includesFlights: isFlightsSelected, packageOptionPrice: currentPrice }))
      }
    } else {
      trackOfferPageEvent('BOOK_NOW_PRESSED')
    }
  }, [canBuyNow, isFlightsSelected, offer, pkg.roomRate?.id, pricing.price, pricingWithFlights?.price, trackOfferPageEvent])

  const onFlexPaymentPolicyOpen = useCallback(async() => {
    showModal(
      <ReserveForZeroAndDepositPolicyModal
        depositAmountPercentage={depositAmountPercentage}
        offerType={offer.type}
        depositThresholds={offer.depositThresholds}
        depositAmount={offer.depositAmount}
      />,
    )
  }, [depositAmountPercentage, offer.depositAmount, offer.depositThresholds, offer.type, showModal])
  const openReserveForZeroPolicyModal = useCallback(() => {
    showModal(<ReserveForZeroPolicyModal />)
  }, [showModal])

  const otherFees = {
    propertyFees: packageView.propertyFees,
    extraGuestsSurcharge: extraGuestSurchargesPayableAtProperty ? packageView.extraGuestsSurcharge : 0,
  }

  const offerLocations = useMemo(() => unique(offer.locations.concat(offer.location)),
    [offer.locations, offer.location])
  const vendorName = offer.vendorName

  const isRewardFareSelected = isRewardFareClass(fareClass as FareClassWithReward)
  const isRewardSelected = rewardAvailable && rewardSeatPointsTotal != null
  const memberId = useAppSelector(state => state.auth.account.memberId)
  const hasLoyaltyAccount = useAppSelector(getHasLoyaltyAccount)
  const rewardLockedForLogin = isRewardFareSelected && !memberId
  const rewardPrice = getRewardPriceDisplayState({ rewardAvailable, rewardSeatPointsTotal, rewardSeatCashCoPayTotal })
  const rewardPriceWithLogin = getRewardPriceDisplayState({ rewardAvailable, rewardSeatPointsTotal, rewardSeatCashCoPayTotal, rewardLockedForLogin })
  const needsRewardPreview = isRewardFareSelected && (!isRewardSelected || rewardLockedForLogin || !hasLoyaltyAccount)
  const userPoints = useAppSelector(state => state.loyalty.account.data?.points ?? 0)
  const rewardPointsDeficit = isRewardFareSelected && rewardSeatPointsTotal != null
    ? Math.max(0, rewardSeatPointsTotal - userPoints)
    : 0
  const hasInsufficientPoints = isRewardFareSelected && !rewardLockedForLogin && rewardPointsDeficit > 0
  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLoyaltyPointsCalculatorHotelOptions(offer, { packageView, currentRegionCode, nights: packageView?.package?.duration }),
    generateLoyaltyPointsCalculatorBundledFlightOptions(offer, {
      packageView,
      flightPrice: effectiveFlightPrice,
      loyaltyEarnKey,
      skip: hideHotelAndFlightsPriceBlock || !!isRewardSelected,
    }),
  ], [offer, packageView, hideHotelAndFlightsPriceBlock, isRewardSelected, currentRegionCode, effectiveFlightPrice, loyaltyEarnKey])

  const urgencyLabels = useMemo(() => offer.urgencyTags.filter(tag => tag.type !== 'new'), [offer.urgencyTags])

  const convertToDisplay = getPriceDisplayFunction(priceMode, rooms)

  const totalGuests = useMemo(() => sumUpOccupancies(rooms), [rooms])
  const guestsDisplayText = useMemo(() => {
    if (!charterBundleRoute || !totalGuests) return undefined
    return formatGuestsDisplay(totalGuests, intl)
  }, [charterBundleRoute, totalGuests, intl])

  const hotelOnlyBookNowText = rooms.length > 1
    ? intl.formatMessage(
        {
          id: '5dzRfOBflj8W_2IJ9z0n',
          defaultMessage: 'Book {count, plural, one {# room} other {# rooms}}',
          description: 'Button text to book multiple rooms on the hotel or room booking page',
        },
        { count: rooms.length },
      )
    : intl.formatMessage({
        id: 'mpFHvG15p8mwo5wMs8b2',
        defaultMessage: 'Book now',
        description: 'Call-to-action button text to proceed with booking the current offer',
      })

  const checkAvailabilityText = intl.formatMessage({
    id: '_etWrwIdpiw96KvQ_Pzp',
    defaultMessage: 'Check availability',
    description: 'Button text to check availability on the hotel or room booking page',
  })

  const hotelBookButtonText = canBuyNow ? hotelOnlyBookNowText : checkAvailabilityText

  const bookNowText = intl.formatMessage({
    id: 'mpFHvG15p8mwo5wMs8b2',
    defaultMessage: 'Book now',
    description: 'Call-to-action button text to proceed with booking the current offer',
  })

  const flightsBookButtonText = canBuyNow ? bookNowText : checkAvailabilityText

  const selectAirlinesMessage = isIndiaRegion
    ? intl.formatMessage({
        id: '5iOeIhI8sPXCW6dROG6I',
        defaultMessage: 'Select from the best available airlines at the next step.',
        description: 'Message for India region about selecting airlines on the offer detail page',
      })
    : intl.formatMessage({
        id: 'At2Utnz5uFmsiwKXM25W',
        defaultMessage: 'Select from all major airlines at the next step.',
        description: 'Message for non-India regions about selecting airlines',
      })

  let priceContent: ReactNode = null

  if (fetching || (isFlightsSelected || charterBundleRoute) && flightPriceLoading) {
    // WAIT FOR PRICES
  } else if (shouldShowCharterFlightErrorMessage) {
    // NO PRICE TO SHOW — check-in matches charter date but flight price failed
  } else if (charterBundleRoute && charterPricingToDisplay.pricing && shouldUseFallbackPricing) {
    // CHARTER BUNDLE FALLBACK PRICE
    const charterDisplayPrice = convertToDisplay(charterPricingToDisplay.pricing.price)
    const charterDisplayMemberPrice = charterPricingToDisplay.memberPricing?.price
      ? convertToDisplay(charterPricingToDisplay.memberPricing.price)
      : undefined
    const charterDisplayValue = convertToDisplay(charterPricingToDisplay.pricing.value)
    let charterNightsCaption: string
    if (isFeederIncluded && charterPricingToDisplay.isUsingFallback) {
      charterNightsCaption = intl.formatMessage(
        {
          id: 'oJywh3Zl_eURnEcbimf8',
          defaultMessage: '{count, plural, one {# night} other {# nights}} & {code} return flights from',
          description: 'Charter bundle price caption showing nights and feeder airport code for non-Melbourne departures',
        },
        { count: pkg.duration, code: feederOriginAirportCode },
      )
    } else if (isFeederIncluded) {
      charterNightsCaption = intl.formatMessage(
        {
          id: 'i0essjbz02dZ5hi_BAcL',
          defaultMessage: '{count, plural, one {# night} other {# nights}} & {code} return flights',
          description: 'Charter bundle price caption showing nights and feeder airport code for non-Melbourne departures (exact price, no from)',
        },
        { count: pkg.duration, code: feederOriginAirportCode },
      )
    } else if (charterPricingToDisplay.isUsingFallback) {
      charterNightsCaption = intl.formatMessage(
        {
          id: 'xYES8sPAAHMuRjZlTwp7',
          defaultMessage: '{count, plural, one {# night} other {# nights}} & flights from',
          description: 'Duration in nights with flights for charter bundle fallback pricing',
        },
        { count: pkg.duration },
      )
    } else {
      charterNightsCaption = intl.formatMessage(
        {
          id: 'wyrGrugPY5eAR3fARf8b',
          defaultMessage: '{count, plural, one {# night} other {# nights}} & flights',
          description: 'Duration in nights with flights for charter bundle pricing',
        },
        { count: pkg.duration },
      )
    }

    const charterPriceDetailsCta = hasInsufficientPoints
      ? <FlashHotelInsufficientLoyaltyPointsCard rewardPointsDeficit={rewardPointsDeficit} />
      : <FlexAutoGroup direction="horizontal-reverse" wrap="wrap" tabletDirection="vertical" gap={8} noDisplayWhenEmpty>
          <OfferBookButton
            size="medium"
            type="submit"
            disabled={fetching}
            packageView={packageView}
            onSubmit={onSubmit}
          >
            {hotelBookButtonText}
          </OfferBookButton>
          {canAddToUserCart && <OfferAddToCartButton
            size="medium"
            type="submit"
            disabled={fetching}
            packageView={packageView}
          />}
      </FlexAutoGroup>
    priceContent = <VerticalSpacer gap={12} data-testid="charter-pricing">
      <VerticalSpacer gap={4}>
        <LabelGroup>
          <OfferLabels
            offer={offer}
            saveAmount={charterPricingToDisplay.memberPricing ? charterPricingToDisplay.pricing.price - charterPricingToDisplay.memberPricing.price : undefined}
            urgencyLabels={urgencyLabels}
          />
          <AgentHubAdditionalCommissionLabel offerId={offer.id} packageId={pkg.id} />
          {showAgentExclusivePackageLabel && <AgentExclusivePackageLabel />}
        </LabelGroup>
        <div>
          {!isStoreMode && isSpoofed && !charterPricingToDisplay.isUsingFallback && <FlashHotelMarginText
            checkIn={checkInMoment?.format(ISO_DATE_FORMAT)}
            pkg={pkg}
            type="exact"
          />}

          {shouldShowPaidEntirelyWithPointsLabel && <OfferPagePaidEntirelyWithPointsLabel />}
          <PriceRowPriceCaption startIcon={<LinePlaneDepartureIcon />}>
            {charterNightsCaption}
          </PriceRowPriceCaption>
          {needsRewardPreview && (() => {
            const rate = REWARD_PREVIEW_RATE_BY_CABIN[toBaseFareClass(fareClass as FareClassWithReward)]
            if (!rate) return null
            const totalPassengers = sumUpOccupancies(rooms)
            const hotelPrice = pricing.price
            const hotelMemberPrice = memberPricing?.price
            const pts = rate.pointsPerPerson * totalPassengers
            const cash = hotelPrice + rate.cashPerPerson * totalPassengers
            const memberCash = hotelMemberPrice ? hotelMemberPrice + rate.cashPerPerson * totalPassengers : undefined
            const isMember = memberCash !== undefined
            return (
              <Group direction="horizontal" gap={4} verticalAlign="baseline" wrap="wrap">
                <Heading variant="heading4" colour={isMember ? 'lux-plus' : undefined}>
                  <FormattedMessage
                    id="CSWSdBApEKhuJcYSLceT"
                    defaultMessage="{points} + {cash}"
                    description="Reward-seat bundle price on the Flash hotel offer page (charter section), showing the loyalty points portion and the cash co-pay portion combined as one heading."
                    values={{
                      points: <FormatLoyaltyPoints points={convertToDisplay(pts)} abbreviated />,
                      cash: <FormatCurrency value={convertToDisplay(memberCash ?? cash)} format="roundedDollar" />,
                    }}
                  />
                </Heading>
              </Group>
            )
          })()}
          {!needsRewardPreview && rewardPrice.show && (() => {
            const rewardBundlePrice = computeRewardBundlePrice({
              hotelPrice: pricing.price,
              hotelMemberPrice: charterPricingToDisplay.memberPricing?.price,
              rewardPointsTotal: rewardPrice.points,
              rewardCashCoPayTotal: rewardPrice.cash,
            })
            return (
              <RewardBundlePriceDisplay
                points={convertToDisplay(rewardBundlePrice.points)}
                cash={convertToDisplay(rewardBundlePrice.memberCash ?? rewardBundlePrice.cash)}
                nonMemberCash={rewardBundlePrice.memberCash !== undefined ? convertToDisplay(rewardBundlePrice.cash) : undefined}
                unitLabel={intl.formatMessage({ id: 'j--PuUYLTgWUxAxROh0Z', defaultMessage: 'total', description: 'Total pricing unit suffix in the Maldives calendar' })}
                data-testid="offer-package-price-reward"
              />
            )
          })()}
          {!needsRewardPreview && !rewardLockedForLogin && !rewardPrice.show && (<>
            {renderBurnPriceDisplay(charterDisplayPrice, charterDisplayMemberPrice)}
            {!showPointsPrice && <LuxPlusPriceStack
              size="L"
              price={charterDisplayPrice}
              memberPrice={charterDisplayMemberPrice}
              saleUnit={priceMode === 'perPerson' ? 'person' : 'total'}
              testid="offer-package-price"
              offerConfig={offer.luxPlus}
            />}
          </>)}
          <PriceRowTaxesAndFees />
          {showIncludedGuestsExceeded && <AdditionalGuestsPopup
            complex={false}
            description={intl.formatMessage({
              id: 'c49bMGV6K-aAi1z169XQ',
              defaultMessage: '+ Extra guest surcharge',
              description: 'Description for extra guest surcharge on the offer detail page',
            })}
            modalContent={pkg.roomPolicyDescription}
          />}
          {charterPricingToDisplay.pricing.value > charterPricingToDisplay.pricing.price && pkg.shouldDisplayValue && <PriceRowValueDiscountWithCaption
            data-testid="value-box"
            discountPercentage={!offer.isDiscountPillHidden && charterPricingToDisplay.pricing.discountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE ? charterPricingToDisplay.pricing.discountPercent : undefined}
            size="M"
            originalValue={charterDisplayValue}
          />}
          {guestsDisplayText && <PriceRowCaption>{guestsDisplayText}</PriceRowCaption>}
        </div>
        <PriceRowAgentHubCommission
          offerId={offer.id}
          productType={offer.productType}
          kind="plain"
          offerTotal={charterPricingToDisplay.pricing.price - (feesToObject(charterPricingToDisplay.pricing.fees).property ?? 0)}
          packageId={pkg.id}
        />
        <div>
          {showPriceDetails && <OfferPriceDetailsRow
            trigger="price-row"
            triggerSize="M"
            offerPage
            offer={offer}
            duration={pkg.duration}
            propertyFees={feesToObject(charterPricingToDisplay.pricing.fees).property}
            hotelPrice={charterPricingToDisplay.pricing.price - (feesToObject(charterPricingToDisplay.pricing.fees).property ?? 0)}
            otherFees={otherFees}
            rooms={rooms}
            extraGuestMessage={showIncludedGuestsExceeded ? pkg.roomPolicyDescription : ''}
            dueAtPropertyMessage={offer.property?.taxesAndFeesContent ?? ''}
            hotelMemberPrice={charterPricingToDisplay.memberPricing?.price}
            showMemberPrice={!!charterPricingToDisplay.memberPricing && charterPricingToDisplay.memberPricing.price > 0}
            cta={charterPriceDetailsCta}
          />}
          {showReserveForZero && <TextLink
            colour="highlight-primary-dark"
            size="small"
            weight="bold"
            endIcon={<LineInfoCircleIcon colour="primary" />}
            onClick={openReserveForZeroPolicyModal}
          >
            {intl.formatMessage({
              id: '3wQ4yMmF7-2EH4zxp8u0',
              defaultMessage: 'Reserve now for free',
              description: 'Hyperlink text to reserve now for free on the offer detail page',
            })}
          </TextLink>}
        </div>
      </VerticalSpacer>
      <LoyaltyPointsEarnWithBreakdownModal
        calculationRequests={pointsEarnCalculationRequests}
        calculationType={charterPricingToDisplay.isUsingFallback ? 'estimate' : 'exact'}
        itemTitle={packageView.package.roomType.name}
        pointsToApply={burnPointsToApply}
        burnPriceInPoints={burnPriceInPoints}
      />
    </VerticalSpacer>
  } else if ((isFlightsSelected || charterBundleRoute) && flightsEnabled) {
    // FLIGHT PRICE
    let nightsFlightsCaption: string
    if (isFeederIncluded && !checkIn) {
      nightsFlightsCaption = intl.formatMessage(
        {
          id: 'oJywh3Zl_eURnEcbimf8',
          defaultMessage: '{count, plural, one {# night} other {# nights}} & {code} return flights from',
          description: 'Charter bundle price caption showing nights and feeder airport code for non-Melbourne departures',
        },
        { count: pkg.duration, code: feederOriginAirportCode },
      )
    } else if (isFeederIncluded) {
      nightsFlightsCaption = intl.formatMessage(
        {
          id: 'i0essjbz02dZ5hi_BAcL',
          defaultMessage: '{count, plural, one {# night} other {# nights}} & {code} return flights',
          description: 'Charter bundle price caption showing nights and feeder airport code for non-Melbourne departures (exact price, no from)',
        },
        { count: pkg.duration, code: feederOriginAirportCode },
      )
    } else if (checkIn) {
      nightsFlightsCaption = intl.formatMessage(
        {
          id: 'GqbB7JWcfzZ12OjgClTq',
          defaultMessage: '{count, plural, one {# night} other {# nights}} & flights',
          description: 'Duration in nights with flights for flash hotel with dates selected',
        },
        { count: pkg.duration },
      )
    } else {
      nightsFlightsCaption = intl.formatMessage(
        {
          id: 'vpYMnAKrJhtV5fqknuZI',
          defaultMessage: '{count, plural, one {# night} other {# nights}} & flights from',
          description: 'Duration in nights with flights from for flash hotel without dates',
        },
        { count: pkg.duration },
      )
    }
    priceContent = <VerticalSpacer gap={12} data-testid="flights-pricing">
      {showFlightsDealTag && activeDeal && <FlightDealLabel activeDeal={activeDeal} showCountdown={false} />}
      {showAgentExclusivePackageLabel && <LabelGroup>
        <AgentExclusivePackageLabel />
      </LabelGroup>}
      <div>
        {hasFlightPrice && <>
          {shouldShowPaidEntirelyWithPointsLabel && <OfferPagePaidEntirelyWithPointsLabel />}
          <PriceRowPriceCaption>
            <LinePlaneDepartureIcon size="S" /> {nightsFlightsCaption}
          </PriceRowPriceCaption>
          {rewardLockedForLogin && (
            <BodyText variant="medium" colour="neutral-two">
              {intl.formatMessage({
                id: 'zp0O9z2ci6Dk9LPEDw0u',
                defaultMessage: 'Log in to view',
                description: 'Message shown on reward fare cards when the user is not logged in',
              })}
            </BodyText>
          )}
          {rewardPriceWithLogin.show && (() => {
            const rewardBundlePrice = computeRewardBundlePrice({
              hotelPrice: pricing.price,
              hotelMemberPrice: memberPricing?.price,
              rewardPointsTotal: rewardPriceWithLogin.points,
              rewardCashCoPayTotal: rewardPriceWithLogin.cash,
            })
            return (
              <RewardBundlePriceDisplay
                points={convertToDisplay(rewardBundlePrice.points)}
                cash={convertToDisplay(rewardBundlePrice.memberCash ?? rewardBundlePrice.cash)}
                nonMemberCash={rewardBundlePrice.memberCash !== undefined ? convertToDisplay(rewardBundlePrice.cash) : undefined}
                unitLabel={intl.formatMessage({ id: 'j--PuUYLTgWUxAxROh0Z', defaultMessage: 'total', description: 'Total pricing unit suffix in the Maldives calendar' })}
                data-testid="offer-package-price-with-flight-reward"
              />
            )
          })()}
          {!rewardPriceWithLogin.show && renderBurnPriceDisplay(
            convertToDisplay(pricingWithFlights.price),
            memberPricingWithFlights?.price ? convertToDisplay(memberPricingWithFlights.price) : undefined,
          )}
          {!rewardPriceWithLogin.show && !showPointsPrice && <LuxPlusPriceStack
            size="L"
            price={convertToDisplay(pricingWithFlights.price)}
            memberPrice={memberPricingWithFlights?.price && convertToDisplay(memberPricingWithFlights?.price)}
            saleUnit={priceMode === 'perPerson' ? 'person*' : 'total*'}
            testid="offer-package-price-with-flight"
            offerConfig={offer.luxPlus}
          />}
          {!rooms && <PriceRowCaption>
            {intl.formatMessage({
              id: 'eKBIFKgS_aT0v8ga1o0H',
              defaultMessage: 'Two Adults',
              description: 'Caption showing default occupancy of two adults on the offer detail page',
            })}
          </PriceRowCaption>}
          <PriceRowTaxesAndFees />
          {showIncludedGuestsExceeded && <AdditionalGuestsPopup
            complex={false}
            description={intl.formatMessage({
              id: 'c49bMGV6K-aAi1z169XQ',
              defaultMessage: '+ Extra guest surcharge',
              description: 'Description for extra guest surcharge on the offer detail page',
            })}
            modalContent={pkg.roomPolicyDescription}
          />}
          {shouldShowValue && <PriceRowValueDiscountWithCaption
            data-testid="value-box"
            size="M"
            discountPercentage={shouldShowDiscount && !!roomsBeforeSurcharge ? discountPercent : undefined}
            originalValue={convertToDisplay(pricingWithFlights.value)}
          />}
          {guestsDisplayText && <PriceRowCaption>{guestsDisplayText}</PriceRowCaption>}

          <PriceRowAgentHubCommission
            kind="plain"
            offerId={offer.id}
            productType={offer.productType}
            offerLocations={offerLocations}
            vendorName={vendorName}
            offerTotal={pricingWithFlights.price}
            packageId={pkg.id}
          />
        </>}

        {!hasFlightPrice && <>
          {flightOrigin.code === flightDestination?.code && <PriceRowCaption>
            {intl.formatMessage(
              {
                id: 'ui_0SxB-9Lc52kGMUvbg',
                defaultMessage: 'No flights available from {airportName}. Please select another departing airport.',
                description: 'Message when no flights available from selected airport',
              },
              {
                airportName: flightOrigin.name,
              },
            )}
          </PriceRowCaption>}
          {departureTooFarInFuture && <PriceRowCaption>
            {intl.formatMessage({
              id: 'SjlpFqk2nfjBfQqW7_O9',
              defaultMessage: 'Flights can only be booked up to 11 months in advance.',
              description: 'Notification message displayed when departure date is too far in future',
            })}
          </PriceRowCaption>}

          {shouldShowPaidEntirelyWithPointsLabel && <OfferPagePaidEntirelyWithPointsLabel />}
          {checkIn && <PriceRowPriceCaption>
            <LinePlaneDepartureIcon size="S" /> {intl.formatMessage(
              {
                id: 'tKIUdE6YO4jbn_hU0kXM',
                defaultMessage: '{count, plural, one {# night} other {# nights}}',
                description: 'Duration in nights for flash hotel pricing with flight icon and dates',
              },
              { count: pkg.duration },
            )}
          </PriceRowPriceCaption>}
          {!checkIn && <PriceRowPriceCaption>
            <LinePlaneDepartureIcon size="S" /> {intl.formatMessage(
              {
                id: 'qQbESVJVUX_BxUrEYJB6',
                defaultMessage: '{count, plural, one {# night} other {# nights}} from',
                description: 'Duration in nights from for flash hotel pricing with flight icon without dates',
              },
              { count: pkg.duration },
            )}
          </PriceRowPriceCaption>}
          {renderBurnPriceDisplay(
            convertToDisplay(pricing.price),
            memberPricing?.price ? convertToDisplay(memberPricing.price) : undefined,
          )}
          {!showPointsPrice && <LuxPlusPriceStack
            size="L"
            price={convertToDisplay(pricing.price)}
            memberPrice={memberPricing?.price && convertToDisplay(memberPricing?.price)}
            saleUnit={priceMode === 'perPerson' ? 'person' : 'total'}
            testid="offer-package-price-with-flight"
            offerConfig={offer.luxPlus}
          />}
          {!rooms && <PriceRowCaption>
            {intl.formatMessage({
              id: 'eKBIFKgS_aT0v8ga1o0H',
              defaultMessage: 'Two Adults',
              description: 'Caption showing default occupancy of two adults on the offer detail page',
            })}
          </PriceRowCaption>}
          <Caption variant="large">
            {intl.formatMessage({
              id: 'ycXWBKNkdmR-ZAXMqz2Z',
              defaultMessage: '& flights',
              description: 'Caption indicating flights are included in the flight search or booking flow',
            })}
          </Caption>
          {showIncludedGuestsExceeded && <AdditionalGuestsPopup
            complex={false}
            description={intl.formatMessage({
              id: 'c49bMGV6K-aAi1z169XQ',
              defaultMessage: '+ Extra guest surcharge',
              description: 'Description for extra guest surcharge on the offer detail page',
            })}
            modalContent={pkg.roomPolicyDescription}
          />}
          {shouldShowValue && <PriceRowValueDiscountWithCaption
            data-testid="value-box"
            size="M"
            originalValue={convertToDisplay(pricing.value)}
          />}
          {guestsDisplayText && <PriceRowCaption>{guestsDisplayText}</PriceRowCaption>}

          <PriceRowAgentHubCommission
            kind="plain"
            offerId={offer.id}
            productType={offer.productType}
            offerLocations={offerLocations}
            vendorName={vendorName}
            offerTotal={pricing.price}
            packageId={pkg.id}
          />

          {!flightsBundledNoCache && flightsBookable && <PriceRowCaption>
            {selectAirlinesMessage}
          </PriceRowCaption>}
        </>}

        {showPriceDetails && <OfferPriceDetailsRow
          trigger="price-row"
          triggerSize="M"
          offerPage
          offer={offer}
          duration={pkg.duration}
          propertyFees={packageView.propertyFees}
          otherFees={otherFees}
          hotelPrice={packageView.hotelPrice}
          flightPrice={effectiveFlightPrice}
          rooms={rooms}
          extraGuestMessage={showIncludedGuestsExceeded ? pkg.roomPolicyDescription : ''}
          dueAtPropertyMessage={offer.property?.taxesAndFeesContent ?? ''}
          hotelMemberPrice={packageView.hotelMemberPrice}
          showMemberPrice={showMemberPrice}
          cta={<Group
            direction="vertical"
            tabletDirection="horizontal"
            horizontalAlign="stretch"
            tabletHorizontalAlign="end"
            gap={8}
          >
            {canAddToUserCart && !!charterBundleRoute && <OfferAddToCartButton
              size="medium"
              type="submit"
              disabled={fetching}
              packageView={packageView}
              data-buytype="flights"
            />}
            {canAddToUserCart && !charterBundleRoute && <OfferSelectPackageButton
              size="medium"
              type="submit"
              disabled={fetching}
              packageView={packageView}
              data-buytype="flights"
              isCharterBundle={false}
            />}
            {(!canBuyNow || !!charterBundleRoute) && <OfferBookButton
              size="medium"
              type="submit"
              disabled={fetching}
              packageView={packageView}
              data-buytype="flights"
              onSubmit={onSubmit}
            >
              {flightsBookButtonText}
            </OfferBookButton>}
          </Group>}
        />}

        <AtolProtectedTooltip />
      </div>

      <LoyaltyPointsEarnWithBreakdownModal
        calculationRequests={pointsEarnCalculationRequests}
        calculationType={(!checkIn || isFlightsSelected) ? 'estimate' : 'exact'}
        itemTitle={packageView.package.roomType.name}
        pointsToApply={burnPointsToApply}
        burnPriceInPoints={burnPriceInPoints}
      />
    </VerticalSpacer>
  } else {
    // HOTEL PRICE
    const hotelNightsCaption = checkIn
      ? intl.formatMessage(
          {
            id: '2QiWJOWP73WPkcJapjWu',
            defaultMessage: '{count, plural, one {# night} other {# nights}}',
            description: 'Duration in nights for flash hotel hotel-only pricing with dates',
          },
          { count: pkg.duration },
        )
      : intl.formatMessage(
          {
            id: 'EBcBnkXr_D1XZCg2Vb8d',
            defaultMessage: '{count, plural, one {# night} other {# nights}} from',
            description: 'Duration in nights from for flash hotel hotel-only pricing without dates',
          },
          { count: pkg.duration },
        )
    priceContent = <VerticalSpacer gap={12} data-testid="pricing">
      <VerticalSpacer gap={4}>
        <LabelGroup>
          <OfferLabels offer={offer} saveAmount={showMemberPrice ? pricing.price - memberPricing?.price : undefined} urgencyLabels={urgencyLabels} />
          <AgentHubAdditionalCommissionLabel offerId={offer.id} packageId={pkg.id} />
          {showAgentExclusivePackageLabel && <AgentExclusivePackageLabel />}
        </LabelGroup>
        <div>
          {!isStoreMode && isSpoofed && <FlashHotelMarginText
            checkIn={checkInMoment?.format(ISO_DATE_FORMAT)}
            pkg={pkg}
            type="exact"
          />}

          {shouldShowPaidEntirelyWithPointsLabel && <OfferPagePaidEntirelyWithPointsLabel />}
          <PriceRowPriceCaption>
            {hotelNightsCaption}
          </PriceRowPriceCaption>
          {renderBurnPriceDisplay(
            convertToDisplay(pricing.price),
            memberPricing?.price ? convertToDisplay(memberPricing.price) : undefined,
          )}
          {!showPointsPrice && <LuxPlusPriceStack
            size="L"
            price={convertToDisplay(pricing.price)}
            memberPrice={memberPricing?.price && convertToDisplay(memberPricing?.price)}
            saleUnit={priceMode === 'perPerson' ? 'person' : 'total'}
            testid="offer-package-price"
            offerConfig={offer.luxPlus}
          />}
          <PriceRowTaxesAndFees />
          {showIncludedGuestsExceeded && <AdditionalGuestsPopup
            complex={false}
            description={intl.formatMessage({
              id: 'c49bMGV6K-aAi1z169XQ',
              defaultMessage: '+ Extra guest surcharge',
              description: 'Description for extra guest surcharge on the offer detail page',
            })}
            modalContent={pkg.roomPolicyDescription}
          />}
          {shouldShowValue && <PriceRowValueDiscountWithCaption
            data-testid="value-box"
            discountPercentage={shouldShowDiscount && !!roomsBeforeSurcharge ? discountPercent : undefined}
            size="M"
            originalValue={convertToDisplay(pricing.value)}
          />}
          {guestsDisplayText && <PriceRowCaption>{guestsDisplayText}</PriceRowCaption>}
        </div>
        <PriceRowAgentHubCommission
          offerId={offer.id}
          productType={offer.productType}
          kind="plain"
          offerTotal={priceWithoutPropertyFees}
          packageId={pkg.id}
        />
        <div>
          {showPriceDetails && <OfferPriceDetailsRow
            trigger="price-row"
            triggerSize="M"
            offerPage
            offer={offer}
            duration={pkg.duration}
            propertyFees={fees.property}
            hotelPrice={priceWithoutPropertyFees}
            otherFees={otherFees}
            rooms={rooms}
            extraGuestMessage={showIncludedGuestsExceeded ? pkg.roomPolicyDescription : ''}
            dueAtPropertyMessage={offer.property?.taxesAndFeesContent ?? ''}
            hotelMemberPrice={packageView.hotelMemberPrice}
            showMemberPrice={showMemberPrice}
            cta={<FlexAutoGroup direction="horizontal-reverse" wrap="wrap" tabletDirection="vertical" gap={8} noDisplayWhenEmpty>
              <OfferBookButton
                size="medium"
                type="submit"
                disabled={fetching}
                packageView={packageView}
                onSubmit={onSubmit}
              >
                {hotelBookButtonText}
              </OfferBookButton>
              {canAddToUserCart && <OfferAddToCartButton
                size="medium"
                type="submit"
                disabled={fetching}
                packageView={packageView}
              />}
            </FlexAutoGroup>}
          />}
          {showReserveForZero && <TextLink
            colour="highlight-primary-dark"
            size="small"
            weight="bold"
            endIcon={<LineInfoCircleIcon colour="primary" />}
            onClick={openReserveForZeroPolicyModal}
          >
            {intl.formatMessage({
              id: '3wQ4yMmF7-2EH4zxp8u0',
              defaultMessage: 'Reserve now for free',
              description: 'Hyperlink text to reserve now for free on the offer detail page',
            })}
          </TextLink>}
        </div>
      </VerticalSpacer>
      <LoyaltyPointsEarnWithBreakdownModal
        calculationRequests={pointsEarnCalculationRequests}
        calculationType={(!checkIn || isFlightsSelected) ? 'estimate' : 'exact'}
        itemTitle={packageView.package.roomType.name}
        pointsToApply={burnPointsToApply}
        burnPriceInPoints={burnPriceInPoints}
      />
    </VerticalSpacer>
  }

  return <>
    <Group direction="horizontal" gap={8} wrap="wrap" noDisplayWhenEmpty>
      {shouldShowDiscount && !roomsBeforeSurcharge && !!discountPercent && <PriceRowValueDiscount
        type="estimate"
        discountPercentage={discountPercent}
      />}
      {!!roomsBeforeSurcharge &&
        <UrgencyLabel variant="critical2">
          {intl.formatMessage(
            {
              id: 'TKSkkpMGGJNGsmPhAWLf',
              defaultMessage: '{count} left at this price',
              description: 'Urgency label showing rooms left at current price on the offer detail page',
            },
            {
              count: roomsBeforeSurcharge,
            },
          )}
        </UrgencyLabel>}
      {showDepositTag && <SecureWithDepositTagAndModal
        isFlexibleCancellation={isFlexibleCancellation}
        offerType={offer.type}
        depositAmountPercentage={depositAmountPercentage}
        depositThresholds={offer.depositThresholds}
      />}
      {showFlexiblePaymentTag && <Clickable onClick={onFlexPaymentPolicyOpen}>
        <Pill startIcon={<LineClockDollarIcon />} kind="tertiary" variant="primary">
          {intl.formatMessage({
            id: '_WfvoKPJ644Skcza0yYb',
            defaultMessage: 'Flexible payment options',
            description: 'Label for flexible payment options during the checkout and payment process',
          })}
        </Pill>
      </Clickable>}
    </Group>
    {priceContent}
  </>
}

export default withOfferPageAndReduxState((state, appState, ownProps: Props) => ({
  rooms: state.rooms,
  offer: state.offer,
  flightOrigin: state.airport,
  checkIn: state.checkInDate,
  fetching: !!appState.calendar.calendarsLoading[ownProps.packageView.package.uniqueKey],
  isSpoofed: appState.auth.account.isSpoofed,
  isStoreMode: appState.system.storeMode,
  defaultDepositAmountPercentage: getDefaultDepositAmountPercentage(appState),
  isFlightsSelected: getEffectiveCharterBundleRoute(state) ? true : state.flights, // enforce to be true when charter bundle is selected to avoid unexpected behavior
  open: !!state.activeFilter,
  charterBundleRoute: getEffectiveCharterBundleRoute(state),
  priceMode: state.priceMode,
  flightDestination: getOfferDestinationAirport(state, appState),
  fareClass: state.fareClass,
  bundledWithFlightsOnly: getEffectiveBundledWithFlightsOnly(state),
}), FlashHotelOptionPriceDetails)
